import * as React from 'react'
import { styled, css } from 'next-yak'

import * as colors from '../../../../constants/colors'
import * as glyphs from '../../../../icons/glyphs'
import * as styles from '../../../../styles'

import { DefaultRewardImage } from '../DefaultRewardImage'
import { Link } from '../../../Link'
import { RoundButton, type RoundButtonProps } from '../../../button/RoundButton'
import { Tag } from '../../../tag/Tag'
import { ContentToggle } from '../../../ContentToggle'
import { WysiwygContent } from '../../../WysiwygContent'

import { type MembershipCardCTAProps, type MembershipRewardCardProps } from '../type'

function MembershipCardComponent({
  className,
  fullWidth = false,
  maxHeight,
  selected = false,
  isMember = false,
  membership,
  showPlaceholderImage = false,
  showSubscriptionsCount = false,
  cta,
  translations,
}: MembershipRewardCardProps): React.ReactElement<any> {
  const [hasMounted, setHasMounted] = React.useState(false)

  React.useEffect(() => {
    setHasMounted(true)
  }, [])

  const bordered = isMember || membership.isFeatured || membership.isHidden

  return (
    <styles.card.reward.CardWrapper
      data-testid="component:membership-reward-card"
      className={className}
      fullWidth={fullWidth}
      bordered={bordered}
    >
      {isMember ? (
        <styles.card.reward.MemberTag color="blue">{translations.memberTag}</styles.card.reward.MemberTag>
      ) : membership.isFeatured ? (
        <styles.card.reward.MemberTag color="blue">
          <glyphs.stroke.Star size={16} />
          {translations.featuredTag}
        </styles.card.reward.MemberTag>
      ) : membership.isHidden ? (
        <styles.card.reward.MemberTag color="green">{translations.hiddenTag}</styles.card.reward.MemberTag>
      ) : (
        <></>
      )}

      {membership.img ? (
        <styles.card.membership.CardImage src={membership.img} alt={membership.title} />
      ) : showPlaceholderImage ? (
        <DefaultRewardImage />
      ) : null}
      <styles.card.reward.CardTitle>{membership.title}</styles.card.reward.CardTitle>

      {isMember ? (
        <styles.card.membership.MembershipStatus>
          <span>
            <glyphs.fill.Success size={22} />
            <styles.copy.S.regular>{translations.youAreMember}</styles.copy.S.regular>
          </span>
          {cta && cta.type === 'link' && (
            <styles.card.membership.MembershipStatusEditCTA>
              <Link onClick={cta.onClick} kind="secondary">
                {translations.cta}
                <glyphs.stroke.ArrowRight size={18} />
              </Link>
            </styles.card.membership.MembershipStatusEditCTA>
          )}
        </styles.card.membership.MembershipStatus>
      ) : cta ? (
        <>
          {!membership.isFree && membership.stock.available === 0 ? (
            <Cta cta={cta} fitToParent={!fullWidth} disabled>
              {translations.soldOut}
            </Cta>
          ) : (
            <Cta cta={cta} fitToParent={!fullWidth} inverted={membership.isFree || selected} $bordered={selected}>
              {translations.cta}
            </Cta>
          )}
        </>
      ) : null}
      {translations.taxDeduction !== undefined && translations.taxDeduction !== 0 && (
        <styles.card.reward.TaxDeduction>
          {hasMounted && <glyphs.stroke.Percent size={16} />}
          {translations.taxDeduction}
        </styles.card.reward.TaxDeduction>
      )}
      <styles.card.reward.CardContent>
        <ContentToggle maxHeight={maxHeight} translations={translations}>
          <WysiwygContent>
            <styles.card.reward.CardDescription as="div" dangerouslySetInnerHTML={membership.description} />
          </WysiwygContent>
        </ContentToggle>
      </styles.card.reward.CardContent>
      <styles.card.reward.CardFooter>
        {showSubscriptionsCount && translations.members && membership.stock.taken > 0 && (
          <Tag color="blue">
            <glyphs.fill.Heart size={12} />
            {translations.members({ taken: membership.stock.taken })}
          </Tag>
        )}

        {(membership.stock.available ?? 0) > 0 && (
          <styles.card.reward.CardSubsRemaining>
            <Uppercase6XS>{translations.stockAvailable({ available: membership.stock.available! })}</Uppercase6XS>
            <Uppercase6XS>{(membership.stock.available ?? 0) <= 5 && translations.soonSoldOut}</Uppercase6XS>
          </styles.card.reward.CardSubsRemaining>
        )}
      </styles.card.reward.CardFooter>

      {translations.discord && (
        <styles.card.membership.MembershipCardRole>
          <glyphs.thirdParty.Discord size={22} />

          {translations.discord}
        </styles.card.membership.MembershipCardRole>
      )}
    </styles.card.reward.CardWrapper>
  )
}

export const MembershipRewardCard = styled(MembershipCardComponent)`
  ${WysiwygContent} {
    li,
    p,
    strong {
      font-size: 14px;

      &:first-of-type {
        margin-top: 0;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }

  ${styles.card.reward.CardSubsRemaining} {
    justify-content: center;
  }
`

type CtaProps = { cta: MembershipCardCTAProps } & BaseCTAProps

function Cta({ cta, children, ...roundButtonProps }: CtaProps) {
  switch (cta.type) {
    case 'button': {
      const props = { ...cta.props, ...roundButtonProps }
      return <BaseCta {...props}>{children}</BaseCta>
    }
    case 'link': {
      const props = { ...cta.props, ...roundButtonProps }
      return (
        <Link onClick={cta.onClick}>
          <BaseCta {...props}>{children}</BaseCta>
        </Link>
      )
    }
  }
}

type BaseCTAProps = RoundButtonProps & { $bordered?: boolean }

const BaseCta = styled(RoundButton)<BaseCTAProps>`
  align-self: center;

  ${({ $bordered, theme }) => {
    if ($bordered) {
      return css`
        border: 2px solid ${(theme as any)?.colors?.primary ?? colors.BRAND_INDIGO};
      `
    }
  }}

  ${({ fitToParent }) => {
    if (!fitToParent) {
      return css`
        width: fit-content;
      `
    }
  }}
`

const Uppercase6XS = styled(styles.heading.XXXXXXS)`
  color: ${colors.TEXT_SECONDARY};
  text-transform: uppercase;
`
